import { useEffect, useRef, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { IconButton } from './Button';
import { CloseIcon } from '../../icons/ui-icons';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  eyebrow?: ReactNode;
  /** Footer actions — confirm/cancel pairs, launch buttons. */
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** Hide the close affordance for flows that must be resolved by choosing. */
  dismissible?: boolean;
  children: ReactNode;
  className?: string;
}

const SIZES = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-3xl',
} as const;

/**
 * Centred dialog for fleet dispatch, combat reports and confirmations.
 *
 * Handles the three things a dialog must not get wrong: Escape closes it, focus
 * moves inside on open and returns to the trigger on close, and background
 * scroll is locked while it's up. Rendered inline rather than via a portal, so
 * mount it near the root of the HUD shell.
 */
export function Modal({
  open,
  onClose,
  title,
  eyebrow,
  footer,
  size = 'md',
  dismissible = true,
  children,
  className,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  // `onClose`/`dismissible` are read through refs, not the effect's own
  // dependency array — a caller whose `onClose` is recreated on every render
  // (e.g. derived from a `useMemo` that also depends on unrelated state, like
  // a live search query) would otherwise re-run this effect on every one of
  // those renders. That re-focuses `panelRef` each time, which yanks focus
  // out of whatever's actually focused inside the modal (a text input mid-
  // keystroke) even though the modal never actually closed and reopened.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const dismissibleRef = useRef(dismissible);
  dismissibleRef.current = dismissible;

  useEffect(() => {
    if (!open) return;

    restoreRef.current = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && dismissibleRef.current) onCloseRef.current();
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      restoreRef.current?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-void/80 backdrop-blur-sm"
        onClick={dismissible ? onClose : undefined}
        aria-hidden
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
        className={cn(
          'relative w-full rounded-panel border border-edge-strong bg-plate-raised',
          'shadow-panel-lg outline-none',
          SIZES[size],
          className,
        )}
      >
        {title || dismissible ? (
          <div className="flex items-start justify-between gap-4 border-b border-edge-subtle px-5 py-3.5">
            <div className="min-w-0">
              {eyebrow ? (
                <div className="font-display text-label-sm uppercase text-content-muted">
                  {eyebrow}
                </div>
              ) : null}
              {title ? (
                <h2 className="font-display text-sm font-semibold uppercase tracking-[0.12em] text-content">
                  {title}
                </h2>
              ) : null}
            </div>
            {dismissible ? (
              <IconButton
                label="Schließen"
                variant="ghost"
                size="sm"
                onClick={onClose}
                icon={<CloseIcon size={16} />}
              />
            ) : null}
          </div>
        ) : null}

        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">{children}</div>

        {footer ? (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-edge-subtle px-5 py-3.5">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}
